// A single, self-contained HTML document for one intel readout.
//
// The SAME markup is used two ways, which is the whole point of the multiscreen
// showcase:
//   1. as the `srcdoc` of an in-page <iframe> popout (sandboxed, on the stage), and
//   2. written into a real `window.open()` child window once a popout is "torn
//      off" and can be dragged onto another monitor / off the screen entirely.
//
// Because it is one string with inline CSS + a tiny inline script, it renders
// identically whether it lives in an iframe or its own OS window, and it needs
// no bundler, no same-origin, and no network.

import type { IntelItem } from "../vision/intel";

const ACCENT: Record<string, string> = {
  science: "#39d7ff",
  fringe: "#b96bff",
  research: "#39ffa0",
  anomaly: "#ffb347",
  readout: "#ff2f4a",
};

/** Accent colour for an intel kind (matches the on-stage popout borders). */
export function accentFor(kind: string): string {
  return ACCENT[kind] ?? "#ff2f4a";
}

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] ?? c);
}

export interface DocOpts {
  /** Full-window layout (big type, screen label, live coords) vs. small popout. */
  fullscreen?: boolean;
  /** Screen / node label shown in the footer, e.g. "SCREEN 2 · DELL U2720Q". */
  label?: string;
}

/**
 * Build the standalone HUD document for one intel item. The inline script prints
 * this window's *live* screen coordinates and size every 400ms, so when the demo
 * director moves and resizes the window across monitors you literally watch the
 * numbers change — proof the app is placing itself on real screen real estate.
 */
export function popoutDocHtml(item: IntelItem, opts: DocOpts = {}): string {
  const c = accentFor(item.kind);
  const big = opts.fullscreen ?? false;
  const label = esc(opts.label ?? "LIETOME // INTEL NODE");
  return `<!doctype html><html><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<style>
  :root{--c:${c};--bg:#05070c;--dim:#7c8aa0;--text:#e6eef7}
  *{margin:0;box-sizing:border-box}
  html,body{height:100%}
  body{
    background:
      radial-gradient(120% 80% at 50% -10%, rgba(255,255,255,.05), transparent 60%),
      repeating-linear-gradient(0deg, rgba(255,255,255,.020) 0 1px, transparent 1px 3px),
      var(--bg);
    color:var(--text);
    font-family:ui-monospace,Menlo,Consolas,"Roboto Mono",monospace;
    overflow:hidden; position:relative;
    padding:${big ? "clamp(20px,4vw,60px)" : "12px 14px"};
    display:flex; flex-direction:column; gap:${big ? "clamp(10px,1.6vh,22px)" : "5px"};
  }
  body::before{ /* vignette + accent wash */
    content:""; position:fixed; inset:0; pointer-events:none;
    box-shadow: inset 0 0 ${big ? "220px" : "60px"} rgba(0,0,0,.7);
    background: radial-gradient(90% 60% at 50% 0%, color-mix(in srgb, var(--c) 10%, transparent), transparent 70%);
  }
  .b{position:fixed;width:${big ? "26px" : "11px"};height:${big ? "26px" : "11px"};border:2px solid var(--c)}
  .tl{top:8px;left:8px;border-right:0;border-bottom:0}
  .tr{top:8px;right:8px;border-left:0;border-bottom:0}
  .bl{bottom:8px;left:8px;border-right:0;border-top:0}
  .br{bottom:8px;right:8px;border-left:0;border-top:0}
  .row{display:flex;justify-content:space-between;align-items:baseline;gap:10px}
  .tag{color:var(--c);letter-spacing:.22em;text-transform:uppercase;font-size:${big ? "clamp(11px,1.4vw,18px)" : "10px"}}
  .coord{color:var(--dim);font-size:${big ? "clamp(10px,1.1vw,14px)" : "9px"};white-space:nowrap;font-variant-numeric:tabular-nums}
  .title{font-weight:800;line-height:1.14;font-size:${big ? "clamp(24px,4.4vw,72px)" : "13px"};text-wrap:balance}
  .text{color:var(--dim);line-height:1.4;font-size:${big ? "clamp(13px,1.7vw,26px)" : "10.5px"};${big ? "" : "max-height:4.6em;overflow:hidden"}}
  .src{color:color-mix(in srgb,var(--dim) 70%,transparent);font-size:${big ? "clamp(10px,1.1vw,15px)" : "9px"};margin-top:auto;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .foot{display:flex;justify-content:space-between;color:var(--dim);font-size:${big ? "clamp(9px,1vw,13px)" : "8.5px"};letter-spacing:.12em;opacity:.8}
  .scan{position:fixed;left:0;right:0;top:0;height:2px;background:linear-gradient(90deg,transparent,var(--c),transparent);opacity:.5;animation:sweep 2.4s linear infinite}
  @keyframes sweep{0%{top:0;opacity:0}10%{opacity:.6}90%{opacity:.6}100%{top:100%;opacity:0}}
  @media (prefers-reduced-motion:reduce){.scan{animation:none;opacity:.2}}
</style></head><body>
  <span class="b tl"></span><span class="b tr"></span><span class="b bl"></span><span class="b br"></span>
  <span class="scan"></span>
  <div class="row"><span class="tag">▸ ${esc(item.tag)}</span><span class="coord" id="xy">—</span></div>
  <div class="title">${esc(item.title)}</div>
  <div class="text">${esc(item.text)}</div>
  <div class="src">◂ ${esc(item.source)}</div>
  <div class="foot"><span>${label}</span><span id="res">—</span></div>
  <script>
    // Live proof-of-placement readout: this window's real position on the OS
    // desktop, refreshed continuously. Watch it change as the app moves us.
    var xy=document.getElementById('xy'),res=document.getElementById('res');
    function tick(){
      try{
        xy.textContent='@ '+(window.screenX)+','+(window.screenY);
        res.textContent=window.innerWidth+'\\u00d7'+window.innerHeight+' px';
      }catch(e){}
    }
    tick(); setInterval(tick,400);
  </script>
</body></html>`;
}
